import React, { CSSProperties, useEffect, useState } from 'react';
import * as S from './styles';
import { getNestedField } from '../../store/utils';
import TableHeaderCell from './TableHeaderCell';
import TableHeaderCellFilter, {
  TableHeaderCellFilterProps,
} from './TableHeaderCellFilter';
import TableControl from './TableControl';
import { create2DMap } from './utils';
import TableFrame from './SubTableFrame.tsx';
import SubTable from './Subtable';
import notFoundImage from './not-found.png';

export interface TableHeader extends TableHeaderCellFilterProps {
  text: string | JSX.Element;
  value: string;
  align?: any;
  mask?: (value: any) => any;
  totalMask?: (value: any) => any;
  color?: (value: any) => string;
  background?: (value: any) => string;
  style?: CSSProperties;
  width?: string;
  table?: TableProps;
  singularLabel?: string;
  pluralLabel?: string;
  isFixed?: boolean;
  fixedPosition?: 'left' | 'right';
  total?: number | string;
  lock?: string;
}

export interface TableProps {
  children?: JSX.Element;
  headers: TableHeader[];
  items: any[];
  handleScroll?: (e: React.UIEvent<HTMLDivElement, UIEvent>) => Promise<void>;
  onLineClick?: (index: number) => any | undefined;
  size?: 'normal' | 'small';
  bodyStyle?: CSSProperties;
  containerStyle?: CSSProperties;
}

const Table = ({
  children,
  headers: h,
  items,
  handleScroll: scroll,
  onLineClick = () => () => {},
  size = 'normal',
  bodyStyle = {},
  containerStyle = {},
}: TableProps) => {
  const [headers, setHeaders] = useState(h);

  useEffect(() => {
    setHeaders(h);
  }, [h]);

  const headFontSize = size === 'normal' ? 20 : 16;
  const cellFontSize = size === 'normal' ? 16 : 14;
  const [cellWidthArray, setCellWidthArray] = useState(headers.map(() => 0));

  const headerGridTemplateColumns = headers.reduce(
    (acc, { width }) => `${acc} ${width ?? '1fr'}`,
    '',
  );

  const [scrollLoading, setScrollLoading] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (scroll && !scrollLoading) {
      const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
      if (Math.floor(scrollHeight - scrollTop) === clientHeight) {
        setScrollLoading(true);
        scroll(e)
          .then(() => {
            setScrollLoading(false);
          })
          .catch(() => {
            setScrollLoading(false);
          });
      }
    }
  };

  const numberOfItems = items.length;
  const numberOfSubtables = headers.filter((header) => header.table).length;

  const [showSubTables, setShowSubTables] = useState(
    create2DMap<boolean>(numberOfItems, numberOfSubtables, false),
  );

  const [subTableHeights, setSubTableHeights] = useState(
    create2DMap(numberOfItems, numberOfSubtables, 0),
  );

  React.useEffect(() => {
    setShowSubTables(
      create2DMap<boolean>(numberOfItems, numberOfSubtables, false),
    );
    setSubTableHeights(create2DMap(numberOfItems, numberOfSubtables, 0));
  }, [numberOfItems, numberOfSubtables]);

  const toggleShow = (x: number, y: number) =>
    setShowSubTables(
      showSubTables.map((row, i) =>
        x === i
          ? row.map((column, j) => (y === j ? !column : false))
          : row.map(() => false),
      ),
    );
  const setSubTableHeight = (x: number, y: number) => (height: number) => {
    setSubTableHeights(
      subTableHeights.map((row, i) =>
        x === i ? row.map((column, j) => (y === j ? height : column)) : row,
      ),
    );
  };

  const fixedLeftArray = headers.map(({ isFixed }, index) =>
    isFixed
      ? `calc(${headers
          .slice(0, index)
          .reduce(
            (acc, { isFixed: fixed }, i) =>
              fixed && cellWidthArray[i]
                ? `${acc} + ${cellWidthArray[i]}`
                : acc,
            '0px',
          )})`
      : undefined,
  );

  const [hoverItem, setHoverItem] = useState(-1);
  const totalArray = headers.map((header) => header.total);
  const hasTotal = totalArray.findIndex((total) => total !== undefined) !== -1;

  return (
    <>
      <S.Container style={{ ...containerStyle }}>
        <S.Table>
          <S.Header>
            <S.LineHead $gridTemplateColumns={headerGridTemplateColumns}>
              {headers.map(
                (
                  { text, style, isFixed, fixedPosition = 'left', ...rest },
                  index,
                ) => (
                  <TableHeaderCell
                    key={index}
                    index={index}
                    setCellWidth={setCellWidthArray}
                    fontSize={headFontSize}
                    fixedPosition={fixedPosition}
                    isFixed={isFixed}
                    left={fixedLeftArray[index]}
                  >
                    <S.TableHeaderCellText style={style}>
                      {text}
                    </S.TableHeaderCellText>
                    <TableHeaderCellFilter {...rest} />
                  </TableHeaderCell>
                ),
              )}
            </S.LineHead>
          </S.Header>

          <S.Body style={{ ...bodyStyle }} onScroll={handleScroll}>
            {items.map((item, itemIndex) => (
              <>
                <S.Line
                  key={itemIndex}
                  $isSelected={!!item.$isSelected}
                  onClick={onLineClick(itemIndex)}
                  $gridTemplateColumns={headerGridTemplateColumns}
                  onMouseEnter={() => setHoverItem(itemIndex)}
                  onMouseLeave={() => setHoverItem(-1)}
                  hover={hoverItem === itemIndex}
                >
                  {headers.map((header, headerIndex) => {
                    const {
                      value,
                      mask,
                      color,
                      background,
                      style,
                      table,
                      singularLabel = 'item',
                      pluralLabel = 'itens',
                      lock,
                    } = header;
                    const isTable = !!table;
                    const nestedValue = value
                      ? getNestedField(item, value)
                      : '';
                    const MaskedValue = mask ? mask(item) : nestedValue;
                    const elementValue = [
                      'string',
                      'number',
                      'object',
                    ].includes(typeof MaskedValue) ? (
                      MaskedValue
                    ) : (
                      <MaskedValue item={item} />
                    );
                    const locked = lock ? getNestedField(item, lock) : false;
                    return (
                      <S.Cell
                        key={`cell ${headerIndex} ${itemIndex}`}
                        fontSize={cellFontSize}
                        isFixed={header?.isFixed}
                        fixedPosition={header?.fixedPosition ?? 'left'}
                        left={fixedLeftArray[headerIndex]}
                        hover={hoverItem === itemIndex}
                        isSelected={!!item.$isSelected}
                        background={background ? background(item) : 'white'}
                        color={color ? color(item) : 'black'}
                        locked={locked}
                        style={header.style}
                      >
                        {!isTable ? (
                          <>{elementValue}</>
                        ) : (
                          <button
                            style={{
                              border: '1px solid rgb(81, 51, 137)',
                              borderRadius: '6px',
                            }}
                            onClick={() => {
                              if (table)
                                toggleShow(
                                  itemIndex,
                                  headers
                                    .filter((header) => header.table)
                                    .findIndex((h) => h === header),
                                );
                            }}
                          >{`${elementValue.length} ${
                            elementValue.length === 1
                              ? singularLabel
                              : pluralLabel
                          }`}</button>
                        )}
                      </S.Cell>
                    );
                  })}
                </S.Line>
                {headers
                  .filter((header) => header.table)
                  .map((header, headerIndex) => (
                    <>
                      <S.SubTableContainer
                        key={`sub ${itemIndex} ${headerIndex}`}
                        show={
                          showSubTables?.[itemIndex]?.[headerIndex] ?? false
                        }
                        height={
                          subTableHeights?.[itemIndex]?.[headerIndex] ?? 0
                        }
                      >
                        <SubTable item={item} header={header} />
                      </S.SubTableContainer>
                    </>
                  ))}
                {headers
                  .filter((header) => header.table)
                  .map((header, headerIndex) =>
                    (showSubTables?.[itemIndex]?.[headerIndex] ?? false) ? (
                      <TableFrame
                        setHeight={(h) => {
                          setSubTableHeight(itemIndex, headerIndex)(h);
                        }}
                      >
                        <SubTable item={item} header={header} />
                      </TableFrame>
                    ) : null,
                  )}
              </>
            ))}
            {hasTotal ? (
              <>
                <S.Line
                  $gridTemplateColumns={headerGridTemplateColumns}
                  style={{
                    position: 'sticky',
                    bottom: '0px',
                    zIndex: '4',
                  }}
                >
                  {headers.map((header, index) => {
                    const {
                      value,
                      color,
                      total = '',
                      totalMask = (v) => v,
                    } = header;
                    return (
                      <S.Cell
                        fontSize={cellFontSize}
                        isFixed={header?.isFixed}
                        fixedPosition={header?.fixedPosition ?? 'left'}
                        left={fixedLeftArray[index]}
                        style={{
                          color: color ? color({ [value]: total }) : 'black',
                          fontWeight: 'bold',
                          ...(() => (header.style ? header.style : {}))(),
                        }}
                      >
                        {totalMask(total)}
                      </S.Cell>
                    );
                  })}
                </S.Line>
              </>
            ) : null}
            {items.length === 0 ? (
              <S.NotFoundImage src={notFoundImage} />
            ) : null}
          </S.Body>
        </S.Table>
      </S.Container>
      {children}
    </>
  );
};

export default Object.assign(Table, {
  Control: TableControl,
});
